import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  Box, Paper, Typography, TextField, Button, 
  IconButton, InputAdornment, CircularProgress, 
  Container, Stack, Divider, styled, Fade,Alert
} from '@mui/material';
import { 
  Visibility, VisibilityOff, Email, 
  PhoneIphone, LockOutlined, ArrowForward 
} from '@mui/icons-material';

/* ─── Design Tokens ───────────────────────────────────────────────────────── */
const T = {
  amber:      '#C2823C',
  amberLight: '#E8A85A',
  cream:      '#FAF8F4',
  ivory:      '#FFFEFB',
  ink:        '#1a1510',
  inkMid:     '#4a3f30',
  inkLight:   '#8a7055',
  border:     'rgba(194,130,60,0.18)',
  shadow:     '0 20px 60px rgba(100,70,30,0.12)',
  error:      '#d32f2f',
};

/* ─── Styled Components ───────────────────────────────────────────────────── */
const LoginWrapper = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: T.cream,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage: `radial-gradient(circle at 20% 30%, rgba(194,130,60,0.05) 0%, transparent 40%),
                      radial-gradient(circle at 80% 70%, rgba(194,130,60,0.05) 0%, transparent 40%)`,
    pointerEvents: 'none',
  }
}));

const LoginCard = styled(Paper)(({ theme }) => ({
  background: T.ivory,
  border: `1px solid ${T.border}`,
  borderRadius: '32px',
  padding: theme.spacing(6, 5),
  width: '100%',
  maxWidth: '450px',
  boxShadow: T.shadow,
  position: 'relative',
  zIndex: 1,
  textAlign: 'center',
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: '50px',
  padding: '12px 0',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  background: `linear-gradient(135deg, ${T.amber} 0%, ${T.amberLight} 100%)`,
  boxShadow: '0 8px 20px rgba(194,130,60,0.25)',
  '&:hover': {
    background: `linear-gradient(135deg, ${T.amberLight} 0%, ${T.amber} 100%)`,
    transform: 'translateY(-1px)',
  },
  '&.Mui-disabled': {
    background: 'rgba(194,130,60,0.4)',
    color: '#fff',
  }
}));

const Login = () => {
  const [data, setData]               = useState({ identifier: '', password: '' });
  const [errors, setErrors]           = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading]     = useState(false);
  const [toastOpen, setToastOpen]     = useState(false);
  const [toastSuccess, setToastSuccess] = useState(false);
  const [message, setMessage]         = useState('');
  const [serverError, setServerError] = useState('');

  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const loginApiUrl = process.env.REACT_APP_LOGIN_API;
  const clientid   = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const scriptUrl  = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

  /* ─── Helpers ─────────────────────────────────────────────────────────── */
  const normalizePhoneNumber = (phone) => {
    if (!phone) return phone;
    let n = phone.replace(/[\s\-()]/g, '');
    if (n.startsWith('+91')) n = n.slice(3);
    else if (n.startsWith('91') && n.length === 12) n = n.slice(2);
    return n;
  };

  const showToast = (msg, success = false) => {
    setMessage(msg);
    setToastSuccess(success);
    setToastOpen(true);
    setTimeout(() => setToastOpen(false), 3500);
  };

  /* ─── Validation ──────────────────────────────────────────────────────── */
  const validate = () => {
    const { identifier, password } = data;
    const errs = {};

    // Identifier
    if (!identifier.trim()) {
      errs.identifier = 'Email or mobile number is required';
    } else if (identifier.includes('@')) {
      if (identifier.length > 254) {
        errs.identifier = 'Email address is too long';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) {
        errs.identifier = 'Enter a valid email address';
      }
    } else {
      const normalized = normalizePhoneNumber(identifier);
      if (!/^\d{10}$/.test(normalized)) {
        errs.identifier = 'Enter a valid 10-digit mobile number';
      }
    }

    // Password
    if (!password) {
      errs.password = 'Password is required';
    } else if (password.length < 6) {
      errs.password = 'Password must be at least 6 characters';
    } else if (password.length > 128) {
      errs.password = 'Password is too long';
    }

    return errs;
  };

  /* ─── onChange — clear field error on edit ────────────────────────────── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
    // Clear the error for this field as the user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /* ─── Submit ──────────────────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run validation
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return; // stop — no API call
    }
    setErrors({});
    setServerError('');   

    setIsLoading(true);
    const { identifier, password } = data;
    const loginData = identifier.includes('@')
      ? { email: identifier.trim(), password }
      : { mobileNumber: normalizePhoneNumber(identifier), password };

    try {
      const response = await axios.post(loginApiUrl, loginData);
      if (response.data.user) {
  const { userId, token } = response.data.user;
  localStorage.setItem('authToken', token);
  localStorage.setItem('userId', userId); 
  showToast('Welcome back to Vimoir!', true);

  // ✅ Redirect back to where the user came from, if provided
  const redirectTo = searchParams.get('redirect');

  setTimeout(() => {
    if (redirectTo) {
      navigate(redirectTo);
    } else {
      navigate("/DashBoard", { state: { userId, info: response.data.user } });
    }
  }, 800);
} else {
  setServerError(response.data.error || 'Unexpected error');
}
    } catch (error) {
      setServerError(error.response?.data?.error || 'Login failed. Please try again.');  // ← was showToast
    } finally {
      setIsLoading(false);
    }
  };

  /* ─── Google Sign-In ──────────────────────────────────────────────────── */
  useEffect(() => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({ client_id: clientid, callback: () => {} });
        window.google.accounts.id.renderButton(
          document.getElementById("google-signin-button"),
          { theme: "outline", size: "large", width: "100%", shape: "pill" }
        );
      }
    };
    document.body.appendChild(script);
  }, []);

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <LoginWrapper>
      <Fade in timeout={800}>
        <LoginCard elevation={0}>

          {/* Logo */}
          <Box mb={4}>
            <Typography variant="h4" sx={{ 
              fontFamily: "'Cormorant Garamond', serif", 
              fontWeight: 600, 
              letterSpacing: '0.1em', 
              color: T.ink 
            }}>
              VIMOIR
            </Typography>
            <Typography variant="body2" sx={{ color: T.inkLight, fontStyle: 'italic' }}>
              Welcome back to your memories
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={3}>

              {/* Identifier field */}
              <TextField
                fullWidth
                label="Email or Mobile"
                name="identifier"
                variant="outlined"
                value={data.identifier}
                onChange={handleChange}
                error={!!errors.identifier}
                helperText={errors.identifier || ''}
                inputProps={{ maxLength: 254 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {data.identifier.includes('@')
                        ? <Email sx={{ color: errors.identifier ? T.error : T.amber }} />
                        : <PhoneIphone sx={{ color: errors.identifier ? T.error : T.amber }} />}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password field */}
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={data.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || ''}
                inputProps={{ maxLength: 128 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: errors.password ? T.error : T.amber }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Forgot password */}
              <Box textAlign="right">
                <Button
                  onClick={() => navigate('/passwordReset')}
                  sx={{ textTransform: 'none', color: T.amber, fontWeight: 600, fontSize: '0.85rem' }}
                >
                  Forgot password?
                </Button>
              </Box>
              {serverError && (
                <Alert
                  severity="error"
                  onClose={() => setServerError('')}
                  sx={{ borderRadius: '12px', textAlign: 'left' }}
                >
                  {serverError}
                </Alert>
              )}

              {/* Submit */}
              <PrimaryButton
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </PrimaryButton>

            </Stack>
          </form>

          <Box my={4}>
            <Divider>
              <Typography variant="caption" sx={{ color: T.inkLight, px: 2, letterSpacing: '0.1em' }}>
                OR CONTINUE WITH
              </Typography>
            </Divider>
          </Box>

          <Box id="google-signin-button" mb={3} />

          <Typography variant="body2" sx={{ color: T.inkMid }}>
            Don't have an account?{' '}
            <Button
              onClick={() => navigate('/register')}
              sx={{ textTransform: 'none', color: T.amber, fontWeight: 700, p: 0, minWidth: 'auto', ml: 0.5 }}
            >
              Create Account
            </Button>
          </Typography>

        </LoginCard>
      </Fade>
    </LoginWrapper>
  );
};

export default Login;